import { SETTINGS_EVENTS } from '../../constants/Events';

const SFX_VOLUME = 0.5;
const VO_VOLUME = 1;
const MUSIC_VOLUME = 0.2;
const BUTTONAUDIO_VOLUME = 0.5;

const SFX_GROUP = 'sfx';
const VO_GROUP = 'vo';
const MUSIC_GROUP = 'music';
const BUTTONAUDIO_GROUP = 'buttonAudio';
const SCENE_ID = 'jggsoundcontroller';

/**
 *
 * @alias components.core.SoundPlugin
 * @classdesc A Sound Controller, used to manage game sounds
 * @todo write documentation
 *
 */
class SoundPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);
    this._events = new Phaser.Events.EventEmitter();
    this._enableLogs = false;
    this.paused = false;

    this.sfxVolume = SFX_VOLUME;
    this.voVolume = VO_VOLUME;
    this.musicVolume = MUSIC_VOLUME;
    this.buttonAudioVolume = BUTTONAUDIO_VOLUME;
    this.audioGroups = {};
    this._tweens = {};
    this.audioGroups[SFX_GROUP] = { sounds: [], volume: this.sfxVolume };
    this.audioGroups[VO_GROUP] = { sounds: [], volume: this.voVolume };
    this.audioGroups[MUSIC_GROUP] = { sounds: [], volume: this.musicVolume };
    this.audioGroups[BUTTONAUDIO_GROUP] = { sounds: [], volume: this.buttonAudioVolume };
  }

  onGameReady() {
    this.game.scene.add(SCENE_ID, {}, true);
    this.game.settings.events.on(SETTINGS_EVENTS.AUDIO_CHANGED, this.handleAudioChanged, this);
    this.handleAudioChanged();
  }

  set enableLogs(value) {
    this._enableLogs = value;
  }

  get events() {
    return this._events;
  }

  get scene() {
    if (!this._scene) {
      this._scene = this.game.scene.getScene(SCENE_ID);
    }
    return this._scene;
  }

  get audioContextLocked() {
    return this.game.sound.locked;
  }

  handleAudioChanged() {
    if (this.game.settings.audio) {
      if (this.game.settings.sfx) {
        this.setGroupVolume(SFX_GROUP, this.sfxVolume);
      } else {
        this.setGroupVolume(SFX_GROUP, 0);
      }
      if (this.game.settings.vo) {
        this.setGroupVolume(VO_GROUP, this.voVolume);
      } else {
        this.setGroupVolume(VO_GROUP, 0);
      }
      if (this.game.settings.music) {
        this.setGroupVolume(MUSIC_GROUP, this.musicVolume);
      } else {
        this.setGroupVolume(MUSIC_GROUP, 0);
      }
      if (this.game.settings.buttonAudio) {
        this.setGroupVolume(BUTTONAUDIO_GROUP, this.buttonAudioVolume);
      } else {
        this.setGroupVolume(BUTTONAUDIO_GROUP, 0);
      }
      this.mute = false;
    } else {
      this.mute = true;
    }
  }
  /**
   * Toggle mute
   * @type {bool}
   */
  set mute(value) {
    this.game.sound.mute = value;
  }

  // SFX - playSFX, playSFXSound, stopSFX, setSFXVolume

  /**
   * Play a Sound Effect from an audiosprite
   * @param {string} spriteID The name of the audiosprite
   * @param {string} id name of clip to play from audiosprite
   * @param {SoundPlayOptions} opts additional playback options
   * @returns { Phaser.Sound.Sound } the sound clip being played
   */
  playSFX(spriteID, id, opts = {}) {
    return this._playAudioSprite(spriteID, id, SFX_GROUP, opts);
  }
  /**
   * Play a Sound Effect direct
   * @param {string} id name of sfx clip to play
   * @param {SoundPlayOptions} opts additional playback options
   * @returns { Phaser.Sound.Sound } the sound clip being played
   */
  playSFXSound(id, opts = {}) {
    return this._playAudioSound(id, SFX_GROUP, opts);
  }

  /**
   * Stop an SFX from playing
   * @param {Phaser.Sound.Sound} audio The SFX clip to stop
   * @param {SoundStopOptions} opts additional stop options
   */
  stopSFX(audio, opts = {}) {
    this._stopAudio(audio, opts);
  }

  /**
   * Stop All Sound EFfects
   * @param {SoundStopOptions} opts
   */
  stopAllSFX(opts = {}) {
    this.stopGroup(SFX_GROUP, opts);
  }

  /**
   * @todo : this method is a bit bizzare - look up what is happening
   */
  setSFXVolume(audio, volume) {
    audio.volume = volume * this.audioGroups[SFX_GROUP].volume;
    this.audioGroups[SFX_GROUP].sounds.forEach((el) => {
      if (el.audio === audio) {
        el.volume = volume;
      }
    });
  }

  // Button Audio - playButtonAudio

  playButtonAudio(spriteID, id, opts = {}) {
    this.stopGroup(BUTTONAUDIO_GROUP);
    return this._playAudioSprite(spriteID, id, BUTTONAUDIO_GROUP, { force: true, ...opts });
  }

  // VO - playVO, stopVO

  _playVOScript() {
    if (!this._voScript || this._voScript.length === 0) {
      return;
    }
    let next = this._voScript.shift();
    let opts = {};
    // check for 'opts' instead of a VO id (i.e. { delay: 800 })
    if (typeof next !== 'string') {
      opts = next;
      next = this._voScript.shift();
    }
    if (next) {
      const audio = this._playAudioSprite(this._voSprite, next, VO_GROUP, { force: true, ...opts });
      audio.once('complete', this._playVOScript, this);
      return audio;
    } else {
      return null;
    }
  }

  /**
   * Play VO clip<br/>Only one VO can be played at a time, will replace any active VO
   * @param {string} spriteID audiosprite to play VO from
   * @param {string} id name of clip to play from audiosprite
   * @param {SoundPlayOptions} opts additional playback options
   * @returns { Phaser.Sound.Sound } the sound clip being played
   */
  playVO(spriteID, id, opts = {}) {
    this.stopVO();
    if (typeof id === 'string') {
      return this._playAudioSprite(spriteID, id, VO_GROUP, { force: true, ...opts });
    } else {
      this._voSprite = spriteID;
      this._voScript = id;
      return this._playVOScript();
    }
  }

  /**
   * Stop any active vo from playing
   * @param {SoundStopOptions} opts
   */
  stopVO(opts = {}) {
    this.stopGroup(VO_GROUP, opts);
    this._voSprite = null;
    this._voScript = null;
  }

  /**
   * Play background music<br/>Only one music can be played at a time, will replace any active music
   * @param {string} id
   * @param {SoundPlayOptions} opts
   * @returns music clip being played
   */

  playMusic(id, opts = {}) {
    this.stopMusic();
    if (!Object.keys(opts).includes('loop')) {
      opts.loop = true;
    }
    return this._playAudioSound(id, MUSIC_GROUP, opts);
  }

  /**
   * Stop music from playing
   * @param {SoundStopOptions} opts
   */
  stopMusic(opts = {}) {
    this.stopGroup(MUSIC_GROUP, opts);
  }

  // internal methods

  _stopTween(audio) {
    if (audio.audioId && this._tweens[audio.audioId]) {
      this._tweens[audio.audioId].remove();
      this._tweens[audio.audioId] = null;
    }
  }

  _fadeOut(audio, duration, onComplete = null, onCompleteScope = null) {
    this._stopTween(audio);
    this._tweens[audio.audioId] = this.scene.tweens.add({
      targets: audio,
      volume: 0,
      duration,
      onComplete,
      onCompleteScope,
    });
  }

  _fadeIn(audio, volume, duration, onComplete = null, onCompleteScope = null) {
    this._stopTween(audio);
    this._tweens[audio.audioId] = this.scene.tweens.add({
      targets: audio,
      volume,
      duration,
      onComplete,
      onCompleteScope,
    });
  }

  _playAudioSound(spriteID, group, opts = {}) {
    if (!this.game.cache.audio.exists(spriteID)) {
      this.logError('SoundController::_playAudioSound not found', spriteID);
      return false;
    }

    this.log('SoundController::_playAudioSound', spriteID, group, opts);

    const groupvolume = this.audioGroups[group].volume || 1;
    const { volume = 1, loop = false, delay = null, fadeIn = false } = opts;
    const audio = this.game.sound.add(spriteID);
    audio.audioId = Phaser.Utils.String.UUID();
    audio.volume = groupvolume * volume;
    if (group) {
      this.addAudioToGroup(audio, group);
    }
    if (fadeIn) {
      const targetVolume = groupvolume * volume;
      if (loop !== false && loop !== true && parseInt(loop) > 0) {
        let playCount = 0;
        audio.on('looped', () => {
          playCount++;
          if (playCount >= loop) {
            audio.stop();
          }
        });
        audio.play({ loop: true, delay, volume: 0 });
      } else {
        audio.play({ loop, delay, volume: 0 });
      }
      this._fadeIn(audio, targetVolume, fadeIn);
    } else {
      if (loop !== false && loop !== true && parseInt(loop) > 0) {
        let playCount = 0;
        audio.on('looped', () => {
          playCount++;
          if (playCount >= loop) {
            audio.stop();
          }
        });
        audio.play({ loop: true, delay, volume: volume * groupvolume });
      } else {
        audio.play({ loop, delay, volume: volume * groupvolume });
      }
    }
    if (this.paused) {
      audio.pause();
    }
    return audio;
  }

  _playAudioSprite(spriteID, id, group, opts = {}) {
    if (!this.game.cache.audio.exists(spriteID)) {
      this.logError('SoundController::_playAudioSprite not found', spriteID);
      return false;
    }

    this.log('SoundController::_playAudioSprite', spriteID, id, group, opts);

    const groupvolume = this.audioGroups[group].volume || 1;
    const { volume = 1, loop = false, delay = null, fadeIn = false } = opts;
    const audioSprite = this.game.sound.addAudioSprite(spriteID);
    audioSprite.audioId = Phaser.Utils.String.UUID();
    audioSprite.volume = groupvolume * volume;
    if (group) {
      this.addAudioToGroup(audioSprite, group);
    }
    if (fadeIn) {
      const targetVolume = audioSprite.volume;
      if (loop !== false && loop !== true && parseInt(loop) > 0) {
        let playCount = 0;
        audioSprite.on('looped', () => {
          playCount++;
          if (playCount >= loop) {
            audioSprite.stop();
          }
        });
        audioSprite.play(id, { loop: true, delay, volume: 0 });
      } else {
        audioSprite.play(id, { loop, delay, volume: 0 });
      }
      this._fadeIn(audioSprite, targetVolume, fadeIn);
    } else {
      if (loop !== false && loop !== true && parseInt(loop) > 0) {
        let playCount = 0;
        audioSprite.on('looped', () => {
          playCount++;
          if (playCount >= loop) {
            audioSprite.stop();
          }
        });
        audioSprite.play(id, { loop: true, delay, volume: groupvolume * volume });
      } else {
        audioSprite.play(id, { loop, delay, volume: groupvolume * volume });
      }
    }
    if (this.paused && !opts.force) {
      audioSprite.pause();
    }
    return audioSprite;
  }

  _stopAudio(audio, opts = {}) {
    const { fadeOut = false } = opts;
    if (fadeOut) {
      this._fadeOut(audio, fadeOut, () => {
        if (audio.key != '') audio.stop();
      });
    } else {
      this._stopTween(audio);
      if (audio.key != '') audio.stop();
    }
  }

  addAudioToGroup(audio, group) {
    if (!this.audioGroups[group]) {
      this.logError('Unknown audio group', group);
      return;
    }
    this.audioGroups[group].sounds.push({ audio: audio, volume: audio.volume });

    audio.volume *= this.audioGroups[group].volume;
    audio.on('stop', () => this.removeAudioFromGroup(audio, group));
  }

  removeAudioFromGroup(audio, group) {
    if (!this.audioGroups[group]) {
      this.logError('Unknown audio group', group);
      return;
    }
    this.audioGroups[group].sounds = this.audioGroups[group].sounds.filter(
      (el) => el.audio !== audio
    );
  }

  /**
   * Set the base volume of an audio group
   * @param {string} group name of group to set audio
   * @param {number} volume volume to play audio at ( 0 - 1 )
   *
   */
  setGroupVolume(group, volume) {
    if (!this.audioGroups[group]) {
      this.logError('Unknown audio group', group);
      return;
    }
    this.audioGroups[group].volume = volume;
    this.audioGroups[group].sounds.forEach((el) => {
      el.audio.volume = el.volume * volume;
    });
  }

  /**
   * Stop an audio group from playing
   * @param {string} group
   * @param {*} opts
   */
  stopGroup(group, opts = {}) {
    if (!this.audioGroups[group]) {
      this.logError('Unknown audio group', group);
      return;
    }
    this.audioGroups[group].sounds.forEach((el) => {
      this._stopAudio(el.audio, opts);
    });
    this.audioGroups[group].sounds = [];
  }

  /**
   * pause an audio group from playing
   * @param {string} group name of group to pause
   */
  pauseGroup(group) {
    if (!this.audioGroups[group]) {
      this.logError('Unknown audio group', group);
      return;
    }
    this.audioGroups[group].sounds.forEach((el) => {
      el.audio.pause();
    });
  }

  /**
   * resume an audio group from paused
   * @param {string} group name of group to resume
   */
  resumeGroup(group) {
    if (!this.audioGroups[group]) {
      this.logError('Unknown audio group', group);
      return;
    }
    this.audioGroups[group].sounds.forEach((el) => {
      el.audio.resume();
    });
  }

  /**
   * Pause all sounds
   */
  pause() {
    this.paused = true;
    Object.keys(this.audioGroups).forEach((group) => {
      if (group != BUTTONAUDIO_GROUP) {
        this.pauseGroup(group);
      }
    });
  }

  /**
   * resume all sounds
   */
  resume() {
    this.paused = false;
    Object.keys(this.audioGroups).forEach((group) => {
      if (group != BUTTONAUDIO_GROUP) {
        this.resumeGroup(group);
      }
    });
  }

  logError(...args) {
    if (this._enableLogs) {
      console.error(...args);
    }
  }

  log(...args) {
    if (this._enableLogs) {
      console.log(...args);
    }
  }
}

/**
 * @typedef SoundPlayOptions
 * @property {number} [volume=1] volume to play audio at ( 0-1 ), will be relative to group volume
 * @property {number|bool} [loop = false] should audio loop, true for indefinately, or number for fixed repeat
 * @property {number} [delay = 0] amount of tuime to delay audio before playing
 * @property {number} [fadeIn = 0] amount of time to fade audio in
 */
/**
 * @namespace components.core.SoundPlugin
 * @typedef SoundStopOptions
 * @property {number} [fadeOut = 0] amount of time to fade audio out
 */

export { SoundPlugin };
